import React, { useState, useEffect, useCallback, useRef } from "react";
import moment from "moment";
import "../../views/BookingFlow/BookingFlow.css";
import { api } from "../../api";
import ConciergeLogo from "../../assets/images/conciergeLogoLight.svg";
import specialOfferBadge from "../../assets/images/special-offer.png";
import Modal from "../common/Modal";
import { loadStripe } from "@stripe/stripe-js";
import { getStripeConfig } from "../../utils/environment";
import {
  selectedBookingDateToIso,
  PUBLIC_HOLIDAY_SURCHARGE_NOTICE,
  computePublicHolidaySurchargeAmount,
  publicHolidaysQueryForLocation,
} from "../../utils/bookingFlowPublicHolidays";
import { formatArrivalTime } from "../../utils/formatArrivalTime";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  isBookingFeePaid,
  isBookingFeePending,
  canCompleteBookingWithoutNewPayment,
} from "../../utils/bookingFlowPayment";

// Get Stripe configuration based on environment
const stripeConfig = getStripeConfig();
const stripePromise = loadStripe(stripeConfig.publishableKey);

// Validate Stripe key in development only
if (process.env.NODE_ENV === "development") {
  stripePromise
    .then((stripe) => {
      if (stripe) {
        console.log(
          "Stripe loaded successfully - Environment:",
          stripeConfig.environment
        );
      }
    })
    .catch((error) => {
      console.error("Stripe loading error:", error);
    });
}

// Payment Form Component
const PaymentForm = ({ cartData, onStepComplete, onGoBack, onCartUpdate }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isLoading, setLoading] = useState(false);
  const [stripeReady, setStripeReady] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    carRego: "",
  });
  const [formErrors, setFormErrors] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    carRego: "",
  });
  const [cardErrors, setCardErrors] = useState({
    cardNumber: "",
    cardExpiry: "",
    cardCvc: "",
  });
  const [cardFormKey, setCardFormKey] = useState(0);
  const [smsOfferAccepted, setSmsOfferAccepted] = useState(null);

  // Generic alert modal state
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    showCancel: false,
    onConfirm: null,
    onCancel: null,
    confirmText: "OK",
    cancelText: "Cancel",
  });
  const [specialOfferModal, setSpecialOfferModal] = useState(null);
  const [publicHolidayByDate, setPublicHolidayByDate] = useState({});
  const checkoutInFlightRef = useRef(false);
  const hasRefreshedCartRef = useRef(false);
  const userEditedFieldsRef = useRef({});

  const bookingFeePaid = isBookingFeePaid(cartData);
  const bookingFeePending = isBookingFeePending(cartData);
  const skipCardPayment = canCompleteBookingWithoutNewPayment(cartData);

  // Ensure step 3 data is saved (fallback) before downstream actions
  const ensureSaveStep3 = async (bookingId) => {
    try {
      const cartId = cartData?._id || cartData?.id;
      if (!cartId) return; // nothing we can do

      const alreadySaved = Boolean(cartData?.step3Data?.email);
      if (alreadySaved) return;

      const fallbackEmail =
        cartData?.step3Data?.email ||
        cartData?.step1Data?.email ||
        formData.email;
      const payload = {
        firstName: formData.firstName || cartData?.step3Data?.firstName || "",
        lastName: formData.lastName || cartData?.step3Data?.lastName || "",
        phone: formData.phone || cartData?.step3Data?.phone || "",
        email: fallbackEmail || "",
        carRego: formData.carRego || cartData?.step3Data?.carRego || "",
        currentStep: 3,
      };

      // Only attempt save if we at least have an email
      if (!payload.email) return;

      await api(`booking-flow/cart/${cartId}/step3`, "PUT", payload);
    } catch (e) {
      console.warn("ensureSaveStep3 failed (non-blocking)", e);
    }
  };

  // Modal helper functions
  const showModal = (
    title,
    message,
    type = "info",
    showCancel = false,
    onConfirm = null,
    onCancel = null,
    confirmText = "OK",
    cancelText = "Cancel"
  ) => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      showCancel,
      onConfirm,
      onCancel,
      confirmText,
      cancelText,
    });
  };

  const showSmsOfferModal = (bookingId) => {
    setSpecialOfferModal({
      onConfirm: async () => {
        await ensureSaveStep3(bookingId);
        try {
          await api(
            `booking-flow/booking/${bookingId}/send-sms-offer`,
            "POST",
            {
              smsOfferAccepted: true,
            }
          );
          console.log("SMS offer sent successfully");
        } catch (error) {
          console.error("Error sending SMS offer:", error);
        }

        setSmsOfferAccepted(true);
        clearCartCookie();
        window.location.href = `/booking-extras/${bookingId}?smsOffer=true`;
      },
      onDecline: () => {
        ensureSaveStep3(bookingId);
        handleSmsOfferDecline(bookingId);
      },
    });
  };

  const handleSmsOfferDecline = (bookingId) => {
    // User declined SMS offer
    setSmsOfferAccepted(false);
    console.log("SMS offer declined");
    clearCartCookie();
    window.location.href = `/booking-extras/${bookingId}?smsOffer=false`;
  };

  const closeModal = () => {
    const title = modal.title || "";
    const message = modal.message || "";
    const wasPaymentError =
      modal.type === "error" &&
      /payment|authentication|card|system error/i.test(`${title} ${message}`);
    setModal((prev) => ({ ...prev, isOpen: false }));
    if (wasPaymentError) {
      resetCardForm();
    }
  };

  const resetCardForm = () => {
    checkoutInFlightRef.current = false;
    setLoading(false);
    setCardFormKey((key) => key + 1);
    setCardErrors({
      cardNumber: "",
      cardExpiry: "",
      cardCvc: "",
    });
  };

  useEffect(() => {
    let cancelled = false;
    const phQuery = publicHolidaysQueryForLocation(
      cartData?.step1Data?.selectedLocation,
    );
    if (!phQuery) {
      setPublicHolidayByDate({});
      return undefined;
    }

    (async () => {
      try {
        const res = await api(`booking-flow/public-holidays?${phQuery}`, "GET");
        if (
          !cancelled &&
          res.data?.success &&
          res.data?.holidaysByDate &&
          typeof res.data.holidaysByDate === "object"
        ) {
          setPublicHolidayByDate(res.data.holidaysByDate);
        }
      } catch (e) {
        console.warn("Could not load public holidays:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cartData?.step1Data?.selectedLocation]);

  // Function to clear cart cookie
  const clearCartCookie = () => {
    document.cookie =
      "ccw_booking_cart_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    console.log("Cart cookie cleared due to 404 error");
  };

  // Function to check URL params for codes
  const checkUrlParamsForCodes = useCallback(
    async (cartId) => {
      if (!cartId) return;

      const queryParams = new URLSearchParams(window.location.search);
      const offerCode = queryParams.get("offer");
      const couponCode = queryParams.get("coupon");
      const rewardCode = queryParams.get("reward");

      // Check offer code
      if (
        offerCode &&
        (!cartData?.offerDetails || cartData.offerDetails.code !== offerCode)
      ) {
        try {
          const response = await api(`offer/?code=${offerCode}`, "GET");
          if (response.status === 200 && response.data.offer !== null) {
            const offer = response.data.offer;
            const updateResponse = await api(
              `booking-flow/cart/${cartId}`,
              "PUT",
              {
                offerDetails: offer,
              }
            );
            if (updateResponse.data.success && updateResponse.data.cart) {
              onCartUpdate(updateResponse.data.cart);
            }
          }
        } catch (error) {
          console.error("Error checking offer:", error);
        }
      }

      // Check coupon code
      if (
        couponCode &&
        (!cartData?.couponDetails || cartData.couponDetails.code !== couponCode)
      ) {
        try {
          const response = await api(`coupon/${couponCode}`, "GET");
          if (response.status === 200 && response.data.coupon !== null) {
            const coupon = response.data.coupon;
            const updateResponse = await api(
              `booking-flow/cart/${cartId}`,
              "PUT",
              {
                couponDetails: coupon,
              }
            );
            if (updateResponse.data.success && updateResponse.data.cart) {
              onCartUpdate(updateResponse.data.cart);
            }
          }
        } catch (error) {
          console.error("Error checking coupon:", error);
        }
      }
    },
    [cartData, onCartUpdate]
  );

  // Load existing user data if available (only when cartData first loads or step3Data changes)
  useEffect(() => {
    if (!cartData) return;

    const step3Data = cartData.step3Data;
    const step1Email = cartData.step1Data?.email;
    const rootEmail = cartData.email;

    if (step3Data || step1Email || rootEmail) {
      console.log("Loading form data from cart:", {
        step1Email,
        step3Email: step3Data?.email,
        rootEmail,
        step3Data,
      });

      setFormData((prev) => {
        const edited = userEditedFieldsRef.current;
        // Checkout (step3) email wins over step1 / root — avoids stale cart cookie email
        const cartEmail =
          step3Data?.email || step1Email || rootEmail || "";

        const pick = (field, cartValue) =>
          edited[field] ? prev[field] : cartValue || prev[field] || "";

        const newFormData = {
          ...prev,
          email: pick("email", cartEmail),
          firstName: pick("firstName", step3Data?.firstName),
          lastName: pick("lastName", step3Data?.lastName),
          phone: pick("phone", step3Data?.phone),
          carRego: pick("carRego", step3Data?.carRego),
        };

        const hasChanges = Object.keys(newFormData).some(
          (key) => newFormData[key] !== prev[key]
        );
        if (hasChanges) {
          console.log("Setting form data:", newFormData);
          return newFormData;
        }
        return prev;
      });
    }
  }, [cartData]); // Now we can safely depend on cartData

  // Check URL params for codes whenever cart data changes
  useEffect(() => {
    if (cartData?._id) {
      checkUrlParamsForCodes(cartData._id);
    }
  }, [cartData?._id, checkUrlParamsForCodes]);

  // Refresh cart from API so paymentStatus survives page reload / return from 3DS
  useEffect(() => {
    const cartId = cartData?._id;
    if (!cartId || hasRefreshedCartRef.current) return;
    hasRefreshedCartRef.current = true;

    (async () => {
      try {
        const res = await api(`booking-flow/cart/${cartId}`, "GET");
        if (res.data?.success && res.data?.cart) {
          onCartUpdate(res.data.cart);
        }
      } catch (err) {
        console.warn("Could not refresh cart payment state:", err);
      }
    })();
  }, [cartData?._id, onCartUpdate]);

  // Check if Stripe is ready
  useEffect(() => {
    if (stripe && elements) {
      setStripeReady(true);
      console.log("Stripe is ready");

      // Test if we can get the card elements
      setTimeout(() => {
        try {
          const cardNumberElement = elements.getElement(CardNumberElement);
          const cardExpiryElement = elements.getElement(CardExpiryElement);
          const cardCvcElement = elements.getElement(CardCvcElement);
          console.log("Card elements found:", {
            cardNumber: !!cardNumberElement,
            cardExpiry: !!cardExpiryElement,
            cardCvc: !!cardCvcElement,
          });
        } catch (error) {
          console.error("Error getting card elements:", error);
        }
      }, 1000);
    } else {
      setStripeReady(false);
    }
  }, [stripe, elements]);

  const isDetailsComplete =
    formData.firstName &&
    formData.lastName &&
    formData.phone &&
    formData.email &&
    formData.carRego;

  const isFormComplete = skipCardPayment
    ? isDetailsComplete
    : isDetailsComplete &&
      !cardErrors.cardNumber &&
      !cardErrors.cardExpiry &&
      !cardErrors.cardCvc;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    userEditedFieldsRef.current[name] = true;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (value.trim()) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleInputBlur = async (e) => {
    const { name, value } = e.target;

    // Only save if there's a value and cartData exists
    if (value && cartData?._id) {
      try {
        console.log(`Saving ${name} to cart:`, value);

        // Update the cart with the current form data
        const response = await api(
          `booking-flow/cart/${cartData._id}/step3`,
          "PUT",
          {
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone,
            email: formData.email,
            carRego: formData.carRego,
            currentStep: 3,
          },
          {
            headers: {
              "x-auto-save": "true",
            },
          }
        );

        if (response.data.success) {
          console.log(`${name} saved successfully`);
          // Don't update cartData for auto-save to prevent form clearing
          // onCartUpdate && onCartUpdate(response.data.cart);
        }
      } catch (error) {
        console.error(`Error saving ${name}:`, error);
        // Clear cookie if it's a 404 error (cart not found)
        if (error.response?.status === 404) {
          clearCartCookie();
        }
        // Don't show error to user for background saves
      }
    }
  };

  const saveUserDetailsToCart = async (paymentMethodId) => {
    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      email: formData.email,
      carRego: formData.carRego,
      currentStep: 3,
    };
    if (paymentMethodId) {
      payload.paymentMethodId = paymentMethodId;
    }
    return api(`booking-flow/cart/${cartData._id}/step3`, "PUT", payload);
  };

  const confirmBookingAfterPayment = async () => {
    const confirmResponse = await api(
      `booking-flow/cart/${cartData._id}/confirm-booking`,
      "POST",
      { userDetails: formData }
    );

    if (confirmResponse.data.success) {
      if (confirmResponse.data.alreadyExists) {
        console.log("Booking already exists, redirecting to success page");
      }
      const bookingId =
        confirmResponse.data.bookingId ||
        confirmResponse.data.booking?._id ||
        confirmResponse.data.booking?.posBookingKey;
      showSmsOfferModal(bookingId);
      return true;
    }

    if (
      confirmResponse.data.refunded &&
      confirmResponse.data.error === "iPos booking failed"
    ) {
      showModal(
        "Booking Could Not Be Completed",
        confirmResponse.data.message +
          "\n\n" +
          confirmResponse.data.supportMessage,
        "error",
        false,
        () => {
          clearCartCookie();
          window.location.href = "/";
        },
        null,
        "Try Again"
      );
    } else {
      showModal(
        "Booking Error",
        "Payment successful but booking confirmation failed. Please contact support.",
        "error"
      );
    }
    return false;
  };

  const handleProcessBookingFeeResponse = async (
    paymentResponse,
    paymentMethod
  ) => {
    if (paymentResponse.data.cart) {
      onCartUpdate(paymentResponse.data.cart);
    }

    if (paymentResponse.data.success || paymentResponse.data.alreadyProcessed) {
      if (paymentResponse.data.alreadyProcessed) {
        console.log(
          "Payment already processed, proceeding to booking confirmation"
        );
      }
      return confirmBookingAfterPayment();
    }

    if (
      paymentResponse.data.requiresAction &&
      paymentResponse.data.clientSecret &&
      paymentMethod &&
      stripe
    ) {
      try {
        const { error: confirmError } = await stripe.confirmCardPayment(
          paymentResponse.data.clientSecret,
          { payment_method: paymentMethod.id }
        );

        if (confirmError) {
          showModal(
            "Payment Authentication Failed",
            confirmError.message ||
              "Payment authentication failed. Please try again.",
            "error"
          );
          return false;
        }

        const syncResponse = await api(
          "booking-flow/process-booking-fee",
          "POST",
          {
            cartId: cartData._id,
            amount: 2.0,
            paymentMethodId: paymentMethod.id,
          }
        );
        return handleProcessBookingFeeResponse(syncResponse, paymentMethod);
      } catch (authError) {
        console.error("Error confirming payment:", authError);
        showModal(
          "Payment Authentication Error",
          "An error occurred during payment authentication. Please try again.",
          "error"
        );
        return false;
      }
    }

    const errorMessage =
      paymentResponse.data.message ||
      paymentResponse.data.error?.message ||
      "Payment failed. Please check your card details and try again.";
    let userMessage = errorMessage;
    if (paymentResponse.data.error) {
      const error = paymentResponse.data.error;
      if (error.declineCode === "insufficient_funds") {
        userMessage =
          "Your card has insufficient funds. Please try a different payment method.";
      } else if (error.code === "expired_card") {
        userMessage = "Your card has expired. Please use a different card.";
      } else if (error.code === "incorrect_cvc") {
        userMessage =
          "The security code on your card is incorrect. Please check and try again.";
      } else if (error.code === "incorrect_number") {
        userMessage =
          "Your card number is incorrect. Please check and try again.";
      }
    }
    showModal("Payment Failed", userMessage, "error");
    return false;
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (checkoutInFlightRef.current) {
      return;
    }

    const { firstName, lastName, phone, email, carRego } = formData;
    console.log("Form data:", formData);

    let hasError = false;
    const newErrors = {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      carRego: "",
    };

    if (!firstName || !firstName.trim()) {
      newErrors.firstName = "Please enter your first name";
      hasError = true;
    }

    if (!lastName || !lastName.trim()) {
      newErrors.lastName = "Please enter your last name";
      hasError = true;
    }

    if (!phone || !phone.trim()) {
      newErrors.phone = "Please enter your phone number";
      hasError = true;
    }

    if (!email || !email.trim() || !email.includes("@")) {
      newErrors.email = "Please enter a valid email address";
      hasError = true;
    }

    if (!carRego || !carRego.trim()) {
      newErrors.carRego = "Please enter your car registration";
      hasError = true;
    }

    const hasCardErrors =
      !skipCardPayment &&
      (cardErrors.cardNumber || cardErrors.cardExpiry || cardErrors.cardCvc);

    if (hasError || hasCardErrors) {
      setFormErrors(newErrors);
      
      let errorMessage = "Please fix the following errors:\n\n";
      if (hasError) {
        errorMessage += "• Fill in all required personal information fields\n";
      }
      if (hasCardErrors) {
        if (cardErrors.cardNumber) {
          errorMessage += `• Card Number: ${cardErrors.cardNumber}\n`;
        }
        if (cardErrors.cardExpiry) {
          errorMessage += `• Card Expiry: ${cardErrors.cardExpiry}\n`;
        }
        if (cardErrors.cardCvc) {
          errorMessage += `• Card CVV: ${cardErrors.cardCvc}\n`;
        }
      }
      
      showModal(
        "Please Fix Errors",
        errorMessage.trim(),
        "warning"
      );

      // Scroll to first error field
      setTimeout(() => {
        const firstErrorField = document.querySelector(
          ".form-group.has-error input, .form-group.has-error select, .stripe-input-wrapper.has-error"
        );
        if (firstErrorField) {
          firstErrorField.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          // Try to focus if it's an input, otherwise focus the card element
          if (firstErrorField.tagName === "INPUT" || firstErrorField.tagName === "SELECT") {
            firstErrorField.focus();
          }
        }
      }, 100);
      return;
    }

    // Clear all errors if validation passes
    setFormErrors({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      carRego: "",
    });

    checkoutInFlightRef.current = true;
    setLoading(true);

    try {
      if (skipCardPayment) {
        const storedPaymentMethodId = cartData.step3Data?.stripePaymentMethodId;
        await saveUserDetailsToCart(storedPaymentMethodId || undefined);

        if (bookingFeePaid) {
          await confirmBookingAfterPayment();
          return;
        }

        if (!storedPaymentMethodId) {
          showModal(
            "Payment required",
            "Please enter your card details below to complete your booking fee.",
            "warning"
          );
          return;
        }

        const paymentResponse = await api(
          "booking-flow/process-booking-fee",
          "POST",
          {
            cartId: cartData._id,
            amount: 2.0,
            paymentMethodId: storedPaymentMethodId,
          }
        );
        await handleProcessBookingFeeResponse(paymentResponse, {
          id: storedPaymentMethodId,
        });
        return;
      }

      if (!stripe || !elements) {
        console.error("Stripe not ready");
        showModal(
          "System Error",
          "Payment system is not ready. Please try again.",
          "error"
        );
        return;
      }

      // Get the card elements
      const cardNumberElement = elements.getElement(CardNumberElement);
      const cardExpiryElement = elements.getElement(CardExpiryElement);
      const cardCvcElement = elements.getElement(CardCvcElement);

      // Validate that all card elements exist
      if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
        console.error("Card elements not found");
        showModal(
          "Payment Error",
          "Payment form is not ready. Please refresh the page and try again.",
          "error"
        );
        setLoading(false);
        return;
      }

      // Check for card field errors before attempting payment
      if (cardErrors.cardNumber || cardErrors.cardExpiry || cardErrors.cardCvc) {
        showModal(
          "Invalid Card Details",
          "Please fix the errors in your card details before proceeding.",
          "warning"
        );
        setLoading(false);
        return;
      }

      console.log("Creating payment method...");
      console.log("Testing Stripe key validity by creating payment method...");

      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
        },
      });

      if (error) {
        console.error("Payment method creation failed:", error);
        console.error(
          "Stripe key validation: FAILED - Payment method creation failed"
        );
        
        // Map Stripe error codes to specific field errors
        let errorMessage = error.message || "An error occurred with your card details.";
        
        // Update card field errors based on error type
        if (error.code === "card_declined" || error.type === "card_error") {
          if (error.message.includes("number") || error.message.includes("card")) {
            setCardErrors(prev => ({ ...prev, cardNumber: errorMessage }));
          } else if (error.message.includes("expiry") || error.message.includes("expiration")) {
            setCardErrors(prev => ({ ...prev, cardExpiry: errorMessage }));
          } else if (error.message.includes("cvc") || error.message.includes("cvv")) {
            setCardErrors(prev => ({ ...prev, cardCvc: errorMessage }));
          } else {
            // General card error - show in all fields or as modal
            setCardErrors({
              cardNumber: errorMessage,
              cardExpiry: "",
              cardCvc: "",
            });
          }
        }
        
        if (
          error.type === "invalid_request_error" &&
          error.message.includes("Invalid API Key")
        ) {
          console.error("ERROR: The Stripe API key is invalid!");
          showModal(
            "Configuration Error",
            "Stripe configuration error: Invalid API key. Please check your Stripe keys.",
            "error"
          );
        } else {
          showModal(
            "Payment Failed",
            errorMessage,
            "error"
          );
        }
        setLoading(false);
        return;
      }

      console.log("Payment method created successfully:", paymentMethod);
      console.log(
        "Stripe key validation: SUCCESS - Payment method creation successful"
      );

      const response = await saveUserDetailsToCart(paymentMethod.id);

      if (response.data.success) {
        const paymentResponse = await api(
          "booking-flow/process-booking-fee",
          "POST",
          {
            cartId: cartData._id,
            amount: 2.0,
            paymentMethodId: paymentMethod.id,
          }
        );
        await handleProcessBookingFeeResponse(paymentResponse, paymentMethod);
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      
      // Handle different error types
      let errorMessage = "An unexpected error occurred. Please try again.";
      
      if (error.response) {
        // API error response
        const status = error.response.status;
        const data = error.response.data;
        const requestUrl = error.config?.url || "";

        if (status === 404) {
          errorMessage = "Your booking session has expired. Please start a new booking.";
          clearCartCookie();
        } else if (status === 409 || data?.capacityError) {
          const capMsg =
            data?.message ||
            data?.error ||
            "This time slot is no longer available.";
          if (requestUrl.includes("confirm-booking")) {
            showModal(
              "Time slot no longer available",
              `${capMsg}\n\nIf a booking fee was charged, please contact us on 1300 279 002 so we can help.`,
              "error",
              false,
              null,
              null,
              "OK"
            );
          } else {
            showModal(
              "Time slot no longer available",
              `${capMsg}\n\nPlease go back and choose a different time.`,
              "warning",
              false,
              async () => {
                try {
                  const clearRes = await api(
                    `booking-flow/cart/${cartData._id}/step1`,
                    "PUT",
                    { selectedTimeSlot: null }
                  );
                  if (clearRes.data?.cart) {
                    onCartUpdate(clearRes.data.cart);
                    onStepComplete(clearRes.data.cart, 1);
                  } else {
                    onStepComplete(cartData, 1);
                  }
                } catch (clearErr) {
                  console.error(clearErr);
                  onStepComplete(cartData, 1);
                }
              },
              null,
              "Choose a new time"
            );
          }
          setLoading(false);
          return;
        } else if (status === 400 || status === 500) {
          // Payment error or validation error
          errorMessage = data?.message || data?.error?.message || "Payment processing failed. Please try again.";
          
          // Check for specific error codes
          if (data?.error?.code === "card_declined") {
            errorMessage = data.error.decline_code === "insufficient_funds"
              ? "Your card has insufficient funds. Please try a different payment method."
              : "Your card was declined. Please try a different payment method or contact your bank.";
          } else if (data?.error?.code === "expired_card") {
            errorMessage = "Your card has expired. Please use a different card.";
          } else if (data?.error?.code === "incorrect_cvc") {
            errorMessage = "The security code on your card is incorrect. Please check and try again.";
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showModal(
        "Payment Error",
        errorMessage,
        "error",
        false,
        null,
        null,
        "Try Again"
      );
    } finally {
      checkoutInFlightRef.current = false;
      setLoading(false);
    }
  };

  const getSelectedService = () => {
    if (cartData?.step2Data?.selectedServices?.length > 0) {
      return cartData.step2Data.selectedServices[0];
    }
    return null;
  };

  const getSelectedLocation = () => {
    return cartData?.step1Data?.selectedLocation;
  };

  const getSelectedDateTime = () => {
    const step1Data = cartData?.step1Data;
    if (step1Data?.selectedDate && step1Data?.selectedTimeSlot) {
      const arrivalTime = formatArrivalTime(step1Data.selectedTimeSlot);
      return `${step1Data.selectedDate} at ${arrivalTime}`;
    }
    return "Date and time not selected";
  };

  const getVehicleType = () => {
    return cartData?.step1Data?.vehicleType || "Not selected";
  };

  const getEstimatedTime = () => {
    const service = getSelectedService();
    return service ? service.duration : "Not available";
  };

  // Calculate discount for the selected service based on offer
  // Function to check if booking date is after offer expiry
  const checkOfferExpiry = (offer, bookingDate) => {
    if (!offer || !offer.endDate || !bookingDate) {
      return { isValid: true }; // No validation needed
    }

    // Parse the booking date
    let parsedBookingDate;
    if (bookingDate === "Today") {
      parsedBookingDate = moment().startOf("day");
    } else if (bookingDate === "Tomorrow") {
      parsedBookingDate = moment().add(1, "day").startOf("day");
    } else if (bookingDate.includes(",") && (bookingDate.includes("st") || bookingDate.includes("nd") || bookingDate.includes("rd") || bookingDate.includes("th"))) {
      // Format like "Wednesday, January 14th 2026"
      parsedBookingDate = moment(bookingDate, "dddd, MMMM Do YYYY");
    } else {
      parsedBookingDate = moment(bookingDate, "YYYY-MM-DD");
    }

    if (!parsedBookingDate.isValid()) {
      return { isValid: true }; // Can't parse date, skip validation
    }

    // Parse offer end date
    const offerEndDate = moment(offer.endDate).endOf("day");

    // Check if booking date is after offer expiry
    if (parsedBookingDate.isAfter(offerEndDate)) {
      return {
        isValid: false,
        offerEndDate: offerEndDate.format("MMMM Do, YYYY"),
        bookingDate: parsedBookingDate.format("MMMM Do, YYYY"),
      };
    }

    return { isValid: true };
  };

  const calculateServiceDiscount = () => {
    const service = getSelectedService();
    if (!service) return null;

    const offer = cartData?.offerDetails;
    const selectedDate = cartData?.step1Data?.selectedDate;
    
    // Check if booking date is after offer expiry
    if (offer && selectedDate) {
      const expiryCheck = checkOfferExpiry(offer, selectedDate);
      if (expiryCheck && !expiryCheck.isValid) {
        // Booking date is after offer expiry - don't apply discount
        console.log("Offer expires before booking date, discount not applied");
        return null;
      }
    }
    if (!offer) return null;

    const selectedLocationId = cartData?.step1Data?.selectedLocationId;

    // Check if offer applies to this service
    let offerApplies = false;

    if (offer.service == null || offer.service === null) {
      // Offer applies to multiple services or all services
      if (offer.services && offer.services.length > 0) {
        const offerServiceIds = offer.services.map((s) => {
          if (s && typeof s === "object") {
            return String(s._id || s.id || s);
          }
          return String(s);
        });
        const serviceIdStr = String(service.id);
        offerApplies = offerServiceIds.some((offerId) => {
          return String(offerId).trim() === serviceIdStr.trim();
        });
      } else {
        // No service restriction, applies to all
        offerApplies = true;
      }
    } else {
      // Offer applies to specific service
      const offerServiceId = offer.service._id
        ? String(offer.service._id)
        : String(offer.service);
      if (String(service.id) === offerServiceId) {
        offerApplies = true;
      }
    }

    // Check location restrictions if offer has them
    if (offerApplies) {
      if (offer.location || (offer.locations && offer.locations.length > 0)) {
        let locationMatches = false;

        if (offer.locations && offer.locations.length > 0) {
          const offerLocationIds = offer.locations.map((l) =>
            String(l._id || l)
          );
          if (
            selectedLocationId &&
            offerLocationIds.includes(String(selectedLocationId))
          ) {
            locationMatches = true;
          }
        } else if (offer.location) {
          const offerLocationId = String(offer.location._id || offer.location);
          if (
            selectedLocationId &&
            String(selectedLocationId) === offerLocationId
          ) {
            locationMatches = true;
          }
        }

        if (!locationMatches) {
          offerApplies = false;
        }
      }
    }

    if (!offerApplies) return null;

    const basePrice = service.price;
    let discountAmount = 0;
    let discountText = "";

    if (offer.type === "fixed") {
      discountAmount = offer.value;
      discountText = `$${offer.value} off`;
    } else if (offer.type === "percent") {
      discountAmount = (basePrice * offer.value) / 100;
      discountText = `${offer.value}% off`;
    } else {
      return null; // nodiscount type
    }

    const discountedPrice = Math.max(0, basePrice - discountAmount);

    return {
      originalPrice: basePrice,
      discountedPrice: discountedPrice,
      discountAmount: discountAmount,
      discountText: discountText,
    };
  };

  const selectedService = getSelectedService();
  const selectedLocation = getSelectedLocation();
  const confirmationDateIso = selectedBookingDateToIso(
    cartData?.step1Data?.selectedDate,
  );
  const confirmationPublicHolidayName =
    confirmationDateIso && publicHolidayByDate[confirmationDateIso]
      ? publicHolidayByDate[confirmationDateIso]
      : null;

  const discountForPricing = calculateServiceDiscount();
  const serviceSubtotalAfterDiscount = selectedService
    ? discountForPricing
      ? discountForPricing.discountedPrice
      : selectedService.price
    : 0;
  const publicHolidaySurchargeAmount = computePublicHolidaySurchargeAmount(
    serviceSubtotalAfterDiscount,
    Boolean(confirmationDateIso && publicHolidayByDate[confirmationDateIso]),
  );
  const estimatedGrandTotal =
    Math.round((serviceSubtotalAfterDiscount + publicHolidaySurchargeAmount) * 100) /
    100;

  return (
    <div className="step4-container">
      <div className="step4-content">
        {/* Header */}
        <div className="step4-header">
          <div className="logo">
            <img
              src={ConciergeLogo}
              alt="Concierge Car Wash"
              className="logo-image"
            />
          </div>
        </div>

        {/* Booking Confirmation Details */}
        <div className="booking-confirmation">
          <h3 className="confirmation-title">Confirm your Booking</h3>
          <div className="confirmation-details">
            <div className="confirmation-item">
              <span className="confirmation-label">Service</span>
              <span className="confirmation-value">
                {selectedService ? selectedService.name : "No service selected"}
              </span>
            </div>
            <div className="confirmation-item">
              <span className="confirmation-label">Location</span>
              <span className="confirmation-value">
                {selectedLocation
                  ? selectedLocation.name
                  : "No location selected"}
              </span>
            </div>
            <div className="confirmation-item">
              <span className="confirmation-label">Booking Date & Time</span>
              <span className="confirmation-value">
                {getSelectedDateTime()}
              </span>
            </div>
            {confirmationPublicHolidayName ? (
              <div
                className="public-holiday-surcharge-notice confirmation-ph-notice"
                role="status"
              >
                <strong>{confirmationPublicHolidayName}</strong>
                <span> — {PUBLIC_HOLIDAY_SURCHARGE_NOTICE}</span>
              </div>
            ) : null}
            <div className="confirmation-item">
              <span className="confirmation-label">Vehicle Type</span>
              <span className="confirmation-value">{getVehicleType()}</span>
            </div>
            <div className="confirmation-item">
              <span className="confirmation-label">
                Estimated Time of Service
              </span>
              <span className="confirmation-value">{getEstimatedTime()}</span>
            </div>
            <div className="confirmation-item">
              <span className="confirmation-label">Service total</span>
              <span className="confirmation-value">
                {discountForPricing ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "8px",
                      justifyContent: "flex-end",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        textDecoration: "line-through",
                        color: "#9ca3af",
                        fontSize: "0.9em",
                      }}
                    >
                      ${discountForPricing.originalPrice.toFixed(2)}
                    </div>
                    <div
                      style={{
                        color: "#ef4444",
                        fontSize: "0.85em",
                        fontWeight: "600",
                      }}
                    >
                      {discountForPricing.discountText}
                    </div>
                    <div style={{ fontWeight: "600", fontSize: "1em" }}>
                      ${serviceSubtotalAfterDiscount.toFixed(2)}
                    </div>
                  </div>
                ) : selectedService ? (
                  `$${serviceSubtotalAfterDiscount.toFixed(2)}`
                ) : (
                  "$0.00"
                )}
              </span>
            </div>
            {publicHolidaySurchargeAmount > 0 ? (
              <div className="confirmation-item">
                <span className="confirmation-label">
                  Public holiday surcharge
                </span>
                <span className="confirmation-value">
                  +${publicHolidaySurchargeAmount.toFixed(2)}
                  <span
                    style={{
                      display: "block",
                      fontSize: "0.78em",
                      color: "#6b7280",
                      fontWeight: "500",
                      marginTop: "4px",
                    }}
                  >
                    10% on service total
                  </span>
                </span>
              </div>
            ) : null}
            <div className="confirmation-item">
              <span className="confirmation-label">Estimated total</span>
              <span className="confirmation-value" style={{ fontWeight: "700" }}>
                {selectedService
                  ? `$${estimatedGrandTotal.toFixed(2)}`
                  : "$0.00"}
              </span>
            </div>
          </div>
        </div>

        {/* Divider between sections */}
        <div className="section-divider"></div>

        {/* User Details Section */}
        <div className="user-details-section">
          <h3 className="user-details-title">Checkout</h3>
          <p className="user-details-subtitle">Your details</p>
        </div>

        {/* User Details Form */}
        <form onSubmit={handlePaymentSubmit} className="user-details-form">
          <div className="form-row">
            <div
              className={`form-group half-width ${
                formErrors.firstName ? "has-error" : ""
              }`}
            >
              <input
                key="firstName-input"
                type="text"
                name="firstName"
                placeholder="First name"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className={formErrors.firstName ? "error" : ""}
                required
              />
              {formErrors.firstName && (
                <div className="booking-flow-error-message">
                  {formErrors.firstName}
                </div>
              )}
            </div>
            <div
              className={`form-group half-width ${
                formErrors.lastName ? "has-error" : ""
              }`}
            >
              <input
                key="lastName-input"
                type="text"
                name="lastName"
                placeholder="Last name"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className={formErrors.lastName ? "error" : ""}
                required
              />
              {formErrors.lastName && (
                <div className="booking-flow-error-message">
                  {formErrors.lastName}
                </div>
              )}
            </div>
          </div>

          <div
            className={`form-group full-width ${
              formErrors.phone ? "has-error" : ""
            }`}
          >
            <input
              key="phone-input"
              type="tel"
              name="phone"
              placeholder="Phone"
              autoComplete="tel"
              value={formData.phone}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              className={formErrors.phone ? "error" : ""}
              required
            />
            {formErrors.phone && (
              <div className="booking-flow-error-message">
                {formErrors.phone}
              </div>
            )}
          </div>

          <div
            className={`form-group full-width ${
              formErrors.email ? "has-error" : ""
            }`}
          >
            <input
              key="email-input" // Static key to prevent re-renders
              type="email"
              name="email"
              placeholder="Email"
              autoComplete="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              className={formErrors.email ? "error" : ""}
              required
            />
            {formErrors.email && (
              <div className="booking-flow-error-message">
                {formErrors.email}
              </div>
            )}
          </div>

          <div
            className={`form-group full-width ${
              formErrors.carRego ? "has-error" : ""
            }`}
          >
            <input
              key="carRego-input"
              type="text"
              name="carRego"
              placeholder="Car Registration"
              autoComplete="off"
              value={formData.carRego}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              className={formErrors.carRego ? "error" : ""}
              required
            />
            {formErrors.carRego && (
              <div className="booking-flow-error-message">
                {formErrors.carRego}
              </div>
            )}
          </div>

          {/* Booking Terms */}
          <div className="booking-terms">
            <p>
              A $2 booking fee will be taken to secure your booking. The $2
              booking fee will be deducted at the time of checkout on premises.
            </p>
            <p>
              We understand your plans may change. If you need to cancel your
              booking, it will result in the forfeit of the booking fee. If you
              do not cancel your booking and do not show to an appointment, an
              additional $50 charge will occur.
            </p>
            <p>
              Note: The total price is an estimate, and an exact quote will be
              given on the day. Prices vary depending on the state, size and
              condition of your vehicle.
            </p>
            <p>
              Note: We will do our best to facilitate your reservation time;
              however, at times, delays can occur. The team will inform you of
              the estimated completion time.
            </p>
          </div>

          {/* Payment Section */}
          <div className="payment-section">
            {bookingFeePaid ? (
              <div
                style={{
                  marginBottom: "16px",
                  padding: "12px 16px",
                  backgroundColor: "#ecfdf5",
                  border: "1px solid #6ee7b7",
                  borderRadius: "8px",
                  color: "#065f46",
                }}
              >
                <strong>Booking fee paid.</strong> Your $2 fee has already been
                taken. Click <strong>Complete booking</strong> below to finish
                your reservation — you won&apos;t be charged again.
              </div>
            ) : bookingFeePending ? (
              <div
                style={{
                  marginBottom: "16px",
                  padding: "12px 16px",
                  backgroundColor: "#fffbeb",
                  border: "1px solid #fcd34d",
                  borderRadius: "8px",
                  color: "#92400e",
                }}
              >
                <strong>Payment in progress.</strong> Click{" "}
                <strong>Complete booking</strong> to continue — you won&apos;t
                be charged again if your fee was already taken.
              </div>
            ) : null}

            {!skipCardPayment ? (
              <>
                <h3 className="payment-title">Pay Now</h3>

                <div className="stripe-card-fields">
                  {stripeReady ? (
                    <div className="card-fields-container" key={cardFormKey}>
                  <div className={`card-number-field ${cardErrors.cardNumber ? "has-error" : ""}`}>
                    <label className="field-label">Card number</label>
                    <div className={`stripe-input-wrapper ${cardErrors.cardNumber ? "has-error" : ""}`}>
                      <CardNumberElement
                        key={`card-number-${cardFormKey}`}
                        options={{
                          style: {
                            base: {
                              fontSize: "16px",
                              color: "#000000",
                              fontFamily:
                                '"Helvetica Neue", Helvetica, sans-serif',
                            },
                            invalid: {
                              color: "#ef4444",
                            },
                          },
                        }}
                        onChange={(event) => {
                          console.log("CardNumberElement onChange:", event);
                          if (event.error) {
                            setCardErrors(prev => ({
                              ...prev,
                              cardNumber: event.error.message,
                            }));
                          } else {
                            setCardErrors(prev => ({
                              ...prev,
                              cardNumber: "",
                            }));
                          }
                        }}
                        onReady={() => {
                          console.log("CardNumberElement is ready!");
                        }}
                      />
                    </div>
                    {cardErrors.cardNumber && (
                      <div className="booking-flow-error-message">
                        {cardErrors.cardNumber}
                      </div>
                    )}
                  </div>
                  <div className="card-expiry-cvc-row">
                    <div className={`card-expiry-field ${cardErrors.cardExpiry ? "has-error" : ""}`}>
                      <label className="field-label">Card expiry</label>
                      <div className={`stripe-input-wrapper ${cardErrors.cardExpiry ? "has-error" : ""}`}>
                        <CardExpiryElement
                          key={`card-expiry-${cardFormKey}`}
                          options={{
                            style: {
                              base: {
                                fontSize: "16px",
                                color: "#000000",
                                fontFamily:
                                  '"Helvetica Neue", Helvetica, sans-serif',
                              },
                              invalid: {
                                color: "#ef4444",
                              },
                            },
                          }}
                          onChange={(event) => {
                            console.log("CardExpiryElement onChange:", event);
                            if (event.error) {
                              setCardErrors(prev => ({
                                ...prev,
                                cardExpiry: event.error.message,
                              }));
                            } else {
                              setCardErrors(prev => ({
                                ...prev,
                                cardExpiry: "",
                              }));
                            }
                          }}
                          onReady={() => {
                            console.log("CardExpiryElement is ready!");
                          }}
                        />
                      </div>
                      {cardErrors.cardExpiry && (
                        <div className="booking-flow-error-message">
                          {cardErrors.cardExpiry}
                        </div>
                      )}
                    </div>
                    <div className={`card-cvc-field ${cardErrors.cardCvc ? "has-error" : ""}`}>
                      <label className="field-label">Card CVV</label>
                      <div className={`stripe-input-wrapper ${cardErrors.cardCvc ? "has-error" : ""}`}>
                        <CardCvcElement
                          key={`card-cvc-${cardFormKey}`}
                          options={{
                            style: {
                              base: {
                                fontSize: "16px",
                                color: "#000000",
                                fontFamily:
                                  '"Helvetica Neue", Helvetica, sans-serif',
                              },
                              invalid: {
                                color: "#ef4444",
                              },
                            },
                          }}
                          onChange={(event) => {
                            console.log("CardCvcElement onChange:", event);
                            if (event.error) {
                              setCardErrors(prev => ({
                                ...prev,
                                cardCvc: event.error.message,
                              }));
                            } else {
                              setCardErrors(prev => ({
                                ...prev,
                                cardCvc: "",
                              }));
                            }
                          }}
                          onReady={() => {
                            console.log("CardCvcElement is ready!");
                          }}
                        />
                      </div>
                      {cardErrors.cardCvc && (
                        <div className="booking-flow-error-message">
                          {cardErrors.cardCvc}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    padding: "16px",
                    color: "#6b7280",
                    textAlign: "center",
                  }}
                >
                  Loading payment form...
                  {!stripeReady && (
                    <div>
                      Please wait while we initialize the payment system...
                    </div>
                  )}
                </div>
              )}
            </div>
              </>
            ) : null}

            <button
              type="submit"
              className="pay-button"
              disabled={
                isLoading ||
                (!skipCardPayment && !stripeReady) ||
                !isFormComplete
              }
            >
              {isLoading
                ? "Processing..."
                : !isFormComplete
                ? "Fill all fields"
                : skipCardPayment
                ? "Complete booking"
                : "Pay $2"}
            </button>
          </div>
        </form>

        {/* Back Button */}
        <div className="step4-actions">
          <button className="back-button" onClick={onGoBack}>
            ← Back to Services
          </button>
        </div>
      </div>

      {specialOfferModal &&
        (() => {
          const selectedService = getSelectedService();
          // Check if service is a detail service by checking the service name or category
          // Detail services typically don't include "wash" in the name
          const serviceName = selectedService?.name?.toLowerCase() || "";
          const isDetailService =
            selectedService?.category === "detailServices" ||
            selectedService?.category === "detail" ||
            (!serviceName.includes("wash") && serviceName.length > 0);
          const washText = isDetailService ? "next wash" : "this wash";

          return (
            <div className="special-offer-overlay">
              <div className="special-offer-container">
                <div className="special-offer-header">
                  <img
                    src={specialOfferBadge}
                    alt="Special Offer"
                    className="special-offer-tag"
                  />
                  <h3>
                    {washText === "next wash"
                      ? "Get Your Next Wash for FREE"
                      : "Get This Wash for FREE"}
                  </h3>
                </div>
                <div className="special-offer-body">
                  <p>
                    Sign up for unlimited wash club and get your {washText}{" "}
                    free. We'll send a text after with a link to claim your free
                    wash.
                  </p>
                </div>
                <div className="special-offer-actions">
                  <button
                    className="accept-btn"
                    onClick={() => {
                      specialOfferModal.onConfirm?.();
                      setSpecialOfferModal(null);
                    }}
                  >
                    Yes, Please
                  </button>
                  <button
                    className="decline-btn"
                    onClick={() => {
                      specialOfferModal.onDecline?.();
                      setSpecialOfferModal(null);
                    }}
                  >
                    No Thanks
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        showCancel={modal.showCancel}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
      />
    </div>
  );
};

// Main Step4 Component with Stripe Elements
const Step4 = ({ cartData, onStepComplete, onGoBack, onCartUpdate }) => {
  if (!stripePromise) {
    return (
      <div className="step4-container">
        <div className="step4-content">
          <div
            style={{ padding: "40px", textAlign: "center", color: "#ef4444" }}
          >
            <h3>Payment System Error</h3>
            <p>
              Unable to load payment system. Please refresh the page or contact
              support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentForm
        cartData={cartData}
        onStepComplete={onStepComplete}
        onGoBack={onGoBack}
        onCartUpdate={onCartUpdate}
      />
    </Elements>
  );
};

export default Step4;
